import { pxToRem } from '@back-office/core';
import { GcpBox, GcpDropdown, GcpTypography } from '@back-office/ui';
import { Button } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles } from '@material-ui/core/styles';
import TextFields from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import HelpOutlineRoundedIcon from '@material-ui/icons/HelpOutlineRounded';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import {
  DESCRIPTION_MAX_LENGTH,
  MAXIMUM_AVAILABLE_AMOUNT,
  MINIMUM_AVAILABLE_AMOUNT,
  REF_1_MAX_LENGTH,
  REF_2_MAX_LENGTH,
} from '../../../constants';
import { CreateQrFieldNames } from '../../../forms';
import useViewModel from './viewmodel';

const useStyles = makeStyles(
  (theme) => ({
    help: {
      marginLeft: theme.spacing(1),
      color: theme.palette.common.hitGray,
      width: pxToRem(16),
      height: pxToRem(16),
    },
  }),
  { name: 'CreateQRCode' }
);

const CreateQRCode: FC = observer(() => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    handleOnChange,
    handleOnSubmit,
    state,
    auth,
    errors,
    clearErrors,
    formState,
    handleSubmit,
    control,
  } = useViewModel();

  return (
    <GcpBox>
      <GcpTypography variant="h3">
        {t('payment.create_qr_payment')}
      </GcpTypography>
      <GcpBox mt={3}>
        <Divider />
      </GcpBox>
      <form autoComplete="off" onSubmit={handleSubmit(handleOnSubmit)}>
        <GcpBox mb={3} mt={3}>
          <Grid item lg={6} xs={12}>
            <GcpTypography variant="body1">
              {t('payment.follow', {
                mid: auth.currentUser.mid,
              })}
            </GcpTypography>
            <GcpBox mt={3}>
              <Controller
                control={control}
                defaultValue={state.data[0]?.value ?? ''}
                name={CreateQrFieldNames.AcqRequesterId}
                render={({ onBlur, onChange, name, value }) => (
                  <GcpDropdown
                    required
                    data={state.data}
                    handleOnBlur={onBlur}
                    handleOnChange={onChange}
                    label={t('payment.terminal')}
                    name={name}
                    value={value}
                  />
                )}
                rules={{ required: 'error.required' }}
              />
            </GcpBox>
            <GcpBox mt={3}>
              <Controller
                control={control}
                defaultValue={state.ref1}
                name={CreateQrFieldNames.Ref1}
                render={({ onChange, name, value, onBlur }) => (
                  <TextFields
                    disabled
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip placement="top" title={<>{t('qr.shop')}</>}>
                            <HelpOutlineRoundedIcon className={classes.help} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                      inputProps: {
                        maxLength: REF_1_MAX_LENGTH,
                      },
                    }}
                    error={!!errors.ref1?.type}
                    helperText={errors.ref1?.message && t(errors.ref1.message)}
                    label={t('payment.ref', { numbers: 1 })}
                    name={name}
                    type="text"
                    value={value}
                    variant="outlined"
                    onBlur={onBlur}
                    onChange={(event) =>
                      onChange(
                        handleOnChange(event.target.value, REF_1_MAX_LENGTH)
                      )
                    }
                  />
                )}
                rules={{ required: 'error.required' }}
              />
            </GcpBox>
            <GcpBox mt={3}>
              <Controller
                control={control}
                defaultValue={auth.currentUser.email}
                name={CreateQrFieldNames.Ref2}
                render={({ onChange, name, value, onBlur }) => (
                  <TextFields
                    disabled
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: REF_2_MAX_LENGTH }}
                    label={t('payment.ref', { numbers: 2 })}
                    name={name}
                    type="text"
                    value={value}
                    variant="outlined"
                    onBlur={onBlur}
                    onChange={(event) =>
                      onChange(
                        handleOnChange(event.target.value, REF_2_MAX_LENGTH)
                      )
                    }
                  />
                )}
              />
            </GcpBox>
            <GcpBox mt={3}>
              <Controller
                control={control}
                defaultValue=""
                name={CreateQrFieldNames.Amount}
                render={({ onChange, name, value, onBlur }) => (
                  <NumberFormat
                    fixedDecimalScale
                    fullWidth
                    isNumericString
                    required
                    thousandSeparator
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">PHP</InputAdornment>
                      ),
                    }}
                    allowNegative={false}
                    customInput={TextFields}
                    decimalScale={2}
                    error={!!errors.amount?.type}
                    helperText={
                      errors.amount?.message && t(errors.amount.message)
                    }
                    label={t('payment.amount')}
                    name={name}
                    placeholder="0.00"
                    type="text"
                    value={value}
                    variant="outlined"
                    onBlur={onBlur}
                    onChange={onChange}
                    onClick={() => clearErrors(CreateQrFieldNames.Amount)}
                  />
                )}
                rules={{
                  required: 'error.required',
                  validate: {
                    min: (value: string) =>
                      +value.replace(/,/g, '') >= MINIMUM_AVAILABLE_AMOUNT ||
                      'error.minimum',
                    max: (value: string) =>
                      +value.replace(/,/g, '') <= MAXIMUM_AVAILABLE_AMOUNT ||
                      'error.maximum',
                  },
                }}
              />
            </GcpBox>
            <GcpBox mt={3}>
              <Controller
                control={control}
                defaultValue=""
                name={CreateQrFieldNames.Description}
                render={({ onChange, name, value, onBlur }) => (
                  <TextFields
                    fullWidth
                    multiline
                    inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                    label={t('payment.description')}
                    name={name}
                    type="text"
                    value={value}
                    variant="outlined"
                    onBlur={onBlur}
                    onChange={onChange}
                  />
                )}
              />
            </GcpBox>
            <GcpBox display="flex" justifyContent="flex-end" mt={3}>
              <Button
                color="primary"
                disabled={!formState.isValid}
                size="large"
                type="submit"
                variant="contained"
              >
                {t('payment.create_qr_code')}
              </Button>
            </GcpBox>
          </Grid>
        </GcpBox>
      </form>
    </GcpBox>
  );
});

export default CreateQRCode;
